import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import { setBundlePdpAovSkus } from '../../../slices/pdp';
import AnimatedProductCard from '../../../components/common/AnimatedProductCard';
import AnimatedVariantSelection from '../../../components/common/AnimatedVariantSelection';
import RichTextDisplay from '../../../components/RichTextDisplay';
import {
  getBundlePdpAovItemsToRender,
  getCheapestVariant,
  getTargetProductWithAllVariantsBySKU,
} from '../../../utils';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import styles from './BundlePdpAOV.module.scss';
import useTealium from '../../../hooks/useTealium';

const BundlePdpAOV = () => {
  const { locale } = useIntl();
  const dispatch = useDispatch();
  const { aovs: allAovs, product } = useDrupalSettings();
  const selectedBundlePdpAOVSKUs = useSelector((state) => state.pdp.selectedBundlePdpAOVSKUs);
  const hasAnItemSelected = useSelector((state) => state.pdp.selectedBundlePdpAOVSKUs.length > 0);
  const selectedTargetProduct = useSelector((state) => state.pdp.selectedSKU);
  const { publish } = drupalPubSubService;

  const bundles = useMemo(() => {
    const findPredicate = (aov) => (
      aov.targetSku === selectedTargetProduct && aov.bundles?.showOnPdp
    );
    const result = allAovs?.find(findPredicate);

    if (result) {
      return result.bundles;
    }
    return {};
  }, [allAovs, selectedTargetProduct]);

  const getDefaultVariant = (variants) => {
    const masterVariant = variants.find((variant) => variant.masterVariant);
    return masterVariant || variants[0];
  };

  const items = useMemo(() => {
    if (bundles.items) {
      return getBundlePdpAovItemsToRender(bundles.items, selectedTargetProduct);
    }

    return [];
  }, [bundles, selectedTargetProduct]);

  const notifyDrupalOfBundleSelection = useCallback(
    (clearValues) => {
      const arrayOfExcludedSkus =
        process.env.PRICE_NOTIFICATION_EXCLUDED_SKUS?.split(',');
      const shouldNotifyDrupal = !arrayOfExcludedSkus?.includes(
        selectedTargetProduct,
      );
      if (shouldNotifyDrupal) {
        const targetProduct = clearValues
          ? product : getTargetProductWithAllVariantsBySKU(
            bundles.items,
            selectedTargetProduct,
          );

        const cheapestVariant = getCheapestVariant(targetProduct.variants);
        const safeDiscount = cheapestVariant.discount?.centAmount ?? 0;

        const basePrice =
          (cheapestVariant.price.centAmount +
            safeDiscount) /
          100;
        const discount = safeDiscount / 100;

        publish({
          subscription: 'onPriceChange',
          parameters: {
            base_price: basePrice,
            discount,
          },
        });
      }
    },
    [publish, selectedTargetProduct, product, bundles],
  );

  const skus = useMemo(() => items
    .reduce((result, currentItem) => {
      const newSkus = currentItem.options.reduce((optionResult, currentOption) => {
        if (currentOption.variants[0].sku === selectedTargetProduct) {
          return optionResult;
        }
        // Get the default variant
        const defaultVariant = currentOption.variants.reduce(
          (variantResult, currentVariant, index) => {
            if (currentVariant.masterVariant || index === 0) {
              return currentVariant;
            }

            return variantResult;
          },
          '');

        return [...optionResult, defaultVariant.sku];
      }, []);

      return [...result, ...newSkus];
    }, []), [items, selectedTargetProduct]);

    useTealium();

  if (bundles && items) {
    const itemElements = items.map((item, index) => {
      const key = item.options[0].productId + index;
      const optionElements = item.options.map((option) => {
        const optionDefaultVariant = getDefaultVariant(option.variants);
        const optionVariantsSKUs = option.variants.map((variant) => variant.sku);
        const selectedVariant = option.variants.reduce((result, current) => {
          if (selectedBundlePdpAOVSKUs.includes(current.sku)) {
            return current;
          }

          return result;
        }, optionDefaultVariant);

        return (
          <div key={option.productId}>
            <AnimatedProductCard
              id={option.productId}
              name={option.productName[locale]}
              title={option.productName[locale]}
              description={option.description[locale]}
              price={selectedVariant.price}
              imageURL={selectedVariant.image}
              discount={selectedVariant.discount}
              displayDiscount={selectedVariant.discount.centAmount > 0}
              onChange={() => {
                if (hasAnItemSelected) {
                  dispatch(setBundlePdpAovSkus([]));
                  notifyDrupalOfBundleSelection(true);
                } else {
                  dispatch(setBundlePdpAovSkus(skus));
                  notifyDrupalOfBundleSelection(false);
                }
              }}
              isSelected={hasAnItemSelected}
            />
            {
              hasAnItemSelected && (
                <AnimatedVariantSelection
                  variants={option.variants}
                  isShowing
                  defaultSelectedSKU={optionDefaultVariant.sku}
                  isShowingDiscountedPrices
                  onSelectVariant={(variant) => {
                    const newSkus = selectedBundlePdpAOVSKUs.map((selectedBundlePdpAOVSKU) => {
                      if (optionVariantsSKUs.includes(selectedBundlePdpAOVSKU)) {
                        return variant.sku;
                      }

                      return selectedBundlePdpAOVSKU;
                    },
                    );
                    dispatch(setBundlePdpAovSkus(newSkus));
                  }}
                />
              )
            }
          </div>
        );
      });

      return (
        <div key={key}>
          {optionElements}
        </div>
      );
    });

    if (bundles.title) {
      return (
        <>
          <div className={styles['bundle-aov-title']}>{bundles.title[locale]}</div>
          {bundles.description[locale] && <RichTextDisplay content={bundles.description[locale]} />}
          {itemElements}
        </>
      );
    }
    return null;
  }

  return null;
};

export default BundlePdpAOV;
